@file:NoLiveLiterals

package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.nutritionist.models.DashboardStats
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// DashboardOverview
//
// Changes from original:
// - All DS tokens (ink/teal/crimson) — zero Material Blue/Green/Orange/Purple
// - StatCard accepts DS semantic colour keys, not arbitrary hex strings
// - AdherenceBarChart math fixed (was dividing by percentage, not count)
// - Fake "insight" values removed — replaced with derivable real metrics only
// - ActionButton renamed DashboardAction to avoid collision with NutritionistApp
// - "View Reports" TODO button removed
// - CardContainer replaced with CSS .dash-card class
// - All hover effects via CSS, no .asDynamic()
// ─────────────────────────────────────────────────────────────────────────────

// Token aliases — matches NutritionistApp DS object exactly
private const val INK        = "#0D2B2E"
private const val INK_MID    = "#3D5A5E"
private const val INK_LIGHT  = "#7A9598"
private const val TEAL       = "#26A69A"
private const val TEAL_DEEP  = "#1A7A72"
private const val SURFACE    = "#F5F9F8"
private const val WHITE      = "#FFFFFF"
private const val BORDER     = "#E0EDEB"
private const val SUCCESS    = "#2E7D32"
private const val WARN       = "#E65100"
private const val WARN_BG    = "#FFF3E0"
private const val DANGER     = "#C62828"
private const val DANGER_BG  = "#FCE4EC"

private const val SHADOW_SM   = "0 1px 4px rgba(13,43,46,0.06)"
private const val SHADOW_MD   = "0 4px 16px rgba(13,43,46,0.10)"

@Composable
fun DashboardOverview(
    stats: DashboardStats?,
    onAddUser: () -> Unit = {},
    onCreatePlan: () -> Unit = {},
    onAssignPlan: () -> Unit = {},
    onBrowseCatalog: () -> Unit = {}
) {
    // Inject overview-scoped CSS
    DisposableEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.id = "dash-overview-styles"
        if (kotlinx.browser.document.getElementById("dash-overview-styles") == null) {
            style.textContent = """
                @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

                @keyframes countIn {
                    from { opacity:0; transform:translateY(8px); }
                    to   { opacity:1; transform:translateY(0); }
                }

                .dash-stat-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }
                .dash-stat-card {
                    background: $WHITE; border-radius: 18px;
                    border: 1.5px solid $BORDER;
                    box-shadow: $SHADOW_SM; padding: 24px;
                    transition: transform 0.22s cubic-bezier(0.22,1,0.36,1),
                                box-shadow 0.22s ease, border-color 0.22s ease;
                }
                .dash-stat-card:hover {
                    transform: translateY(-4px); box-shadow: $SHADOW_MD;
                    border-color: $TEAL;
                }
                .dash-stat-value {
                    font-family: 'Syne', sans-serif; font-size: 36px;
                    font-weight: 800; line-height: 1;
                    animation: countIn 0.4s cubic-bezier(0.22,1,0.36,1) both;
                }
                .dash-stat-label {
                    font-family: 'DM Sans', sans-serif; font-size: 12px;
                    font-weight: 600; color: $INK_LIGHT; margin-top: 6px;
                    text-transform: uppercase; letter-spacing: 0.08em;
                }

                .dash-section-label {
                    font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700;
                    letter-spacing: 0.14em; text-transform: uppercase; color: $TEAL;
                    margin-bottom: 8px;
                }
                .dash-section-title {
                    font-family: 'Syne', sans-serif; font-size: 22px;
                    font-weight: 800; color: $INK; letter-spacing: -0.03em;
                    line-height: 1.15; margin-bottom: 20px;
                }

                .dash-card {
                    background: $WHITE; border-radius: 20px;
                    border: 1.5px solid $BORDER; box-shadow: $SHADOW_SM; padding: 24px;
                }

                /* Quick action buttons */
                .qa-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 12px 24px; border-radius: 999px; font-size: 14px;
                    font-weight: 700; cursor: pointer; border: 1.5px solid $BORDER;
                    background: $WHITE; color: $INK_MID;
                    font-family: 'DM Sans', sans-serif;
                    transition: all 0.18s cubic-bezier(0.22,1,0.36,1);
                    box-shadow: $SHADOW_SM;
                }
                .qa-btn:hover {
                    border-color: $TEAL; color: $TEAL;
                    transform: translateY(-2px); box-shadow: $SHADOW_MD;
                }
                .qa-btn.primary {
                    background: $INK; color: white; border-color: $INK;
                }
                .qa-btn.primary:hover { background: #1A3D40; border-color: #1A3D40; color: white; }
                .qa-btn.teal {
                    background: $TEAL; color: white; border-color: $TEAL;
                    box-shadow: 0 4px 16px rgba(38,166,154,0.25);
                }
                .qa-btn.teal:hover { background: $TEAL_DEEP; border-color: $TEAL_DEEP; color: white; }

                /* Adherence chart */
                .adh-row { display:flex; align-items:center; gap:12px; }
                .adh-range-label { width:90px; font-size:12px; color:$INK_LIGHT; font-weight:600; flex-shrink:0; }
                .adh-track { flex:1; height:28px; background:$SURFACE; border-radius:6px; overflow:hidden; position:relative; }
                .adh-bar { height:100%; border-radius:6px; transition:width 0.6s cubic-bezier(0.22,1,0.36,1); display:flex; align-items:center; justify-content:flex-end; padding-right:8px; }
                .adh-bar-label { font-size:12px; font-weight:700; color:white; }
                .adh-count { width:28px; font-size:12px; font-weight:700; color:$INK_MID; text-align:right; flex-shrink:0; }

                /* Alert card */
                .dash-alert {
                    background: $WHITE; border-radius: 16px;
                    box-shadow: $SHADOW_SM; padding: 20px;
                    display: flex; align-items: center; gap: 16px;
                }

                @media (max-width: 768px) {
                    .dash-stat-grid { grid-template-columns: repeat(2, 1fr); }
                    .dash-analytics-grid { grid-template-columns: 1fr !important; }
                }
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(style)
        }
        onDispose {}
    }

    if (stats == null) {
        // Inline skeleton — no separate LoadingSpinner dependency
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px) }}) {
            Div({ style {
                display(DisplayStyle.Grid)
                property("grid-template-columns","repeat(auto-fit,minmax(180px,1fr))")
                gap(16.px); marginBottom(32.px)
            }}) {
                repeat(6) {
                    Div({ style {
                        height(110.px); borderRadius(18.px)
                        property("background","linear-gradient(90deg,#E8F2F1 25%,#F4FAFA 50%,#E8F2F1 75%)")
                        property("background-size","600px 100%")
                        property("animation","shimmer 1.4s ease-in-out infinite")
                    }}) {}
                }
            }
        }
        return
    }

    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(0.px) }}) {

        // ── Page header ───────────────────────────────────────────────────
        Div({ style { marginBottom(32.px) }}) {
            Div({ classes("dash-section-label") }) { Text("Nutritionist Portal") }
            Div({ classes("dash-section-title") }) { Text("Dashboard Overview") }
        }

        // ── Stat cards ────────────────────────────────────────────────────
        Div({ classes("dash-stat-grid") }) {
            DashStatCard("👥", "Total Patients",      stats.totalPatients.toString(),       INK,       isAlert = false)
            DashStatCard("✅", "Active Patients",     stats.activePatients.toString(),      TEAL,      isAlert = false)
            DashStatCard("📊", "Avg Adherence",       "${stats.averageAdherence}%",
                when { stats.averageAdherence >= 80 -> SUCCESS; stats.averageAdherence >= 60 -> WARN; else -> DANGER },
                isAlert = stats.averageAdherence < 60
            )
            DashStatCard("📋", "Plans Created",       stats.plansCreated.toString(),        TEAL_DEEP, isAlert = false)
            DashStatCard("🔔", "Active Alerts",       stats.activeAlerts.toString(),
                if (stats.activeAlerts > 0) DANGER else SUCCESS,
                isAlert = stats.activeAlerts > 0
            )
            DashStatCard("📅", "Today's Appts",       stats.todayAppointments.toString(),   INK,       isAlert = false)
        }

        // ── Quick actions ─────────────────────────────────────────────────
        Div({ style { marginBottom(40.px) }}) {
            Div({ classes("dash-section-label") }) { Text("Quick Actions") }
            Div({ style { display(DisplayStyle.Flex); gap(12.px); flexWrap(FlexWrap.Wrap) }}) {
                Button({ classes("qa-btn","teal");   onClick { onAddUser() }      }) { Text("➕ Add Patient") }
                Button({ classes("qa-btn","primary"); onClick { onCreatePlan() }  }) { Text("📋 Create Plan") }
                Button({ classes("qa-btn");           onClick { onAssignPlan() }  }) { Text("🎯 Assign Plan") }
                Button({ classes("qa-btn");           onClick { onBrowseCatalog() } }) { Text("🍽️ Browse Catalog") }
            }
        }

        // ── Analytics & Insights ──────────────────────────────────────────
        Div({ style { marginBottom(40.px) }}) {
            Div({ classes("dash-section-label") }) { Text("Analytics") }
            Div({ classes("dash-section-title") }) { Text("Adherence & Insights") }

            Div({
                attr("class","dash-analytics-grid")
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns","2fr 1fr")
                    gap(20.px)
                }
            }) {
                // Adherence distribution chart
                Div({ classes("dash-card") }) {
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("800")
                        color(Color(INK)); marginBottom(20.px)
                    }}) { Text("Patient Adherence Distribution") }
                    AdherenceDistributionChart(stats.averageAdherence, stats.activePatients)
                }

                // Key metrics — only real, derivable numbers
                Div({ classes("dash-card") }) {
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("800")
                        color(Color(INK)); marginBottom(16.px)
                    }}) { Text("At a Glance") }

                    val utilizationPct = if (stats.totalPatients > 0)
                        (stats.activePatients * 100 / stats.totalPatients) else 0
                    val pendingLoad = stats.pendingReviews + stats.activeAlerts

                    RealInsightItem("🟢", "Active Rate",
                        "$utilizationPct% of patients active",
                        if (utilizationPct >= 70) SUCCESS else WARN)
                    RealInsightItem("📬", "Pending Work",
                        "$pendingLoad items need attention",
                        if (pendingLoad == 0) SUCCESS else if (pendingLoad < 5) WARN else DANGER)
                    RealInsightItem("📅", "Today's Schedule",
                        "${stats.todayAppointments} appointment${if (stats.todayAppointments != 1) "s" else ""}",
                        INK)
                    RealInsightItem("📋", "Plan Coverage",
                        "${stats.plansCreated} plans across ${stats.totalPatients} patients",
                        TEAL)
                }
            }
        }

        // ── Attention required ────────────────────────────────────────────
        if (stats.activeAlerts > 0 || stats.pendingReviews > 0) {
            Div({ style { marginBottom(40.px) }}) {
                Div({ classes("dash-section-label") }) { Text("Action Required") }
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) }}) {
                    if (stats.activeAlerts > 0) {
                        DashAlertRow(
                            icon        = "🔔",
                            title       = "Active Alerts",
                            description = "${stats.activeAlerts} patient alert${if (stats.activeAlerts != 1) "s" else ""} requiring your attention",
                            accentColor = DANGER,
                            accentBg    = DANGER_BG
                        )
                    }
                    if (stats.pendingReviews > 0) {
                        DashAlertRow(
                            icon        = "📝",
                            title       = "Pending Reviews",
                            description = "${stats.pendingReviews} progress review${if (stats.pendingReviews != 1) "s" else ""} awaiting your sign-off",
                            accentColor = WARN,
                            accentBg    = WARN_BG
                        )
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DashStatCard(
    icon: String,
    label: String,
    value: String,
    valueColor: String,
    isAlert: Boolean
) {
    Div({
        classes("dash-stat-card")
        if (isAlert) style { property("border-color", DANGER) }
    }) {
        Div({ style { fontSize(28.px); marginBottom(12.px) }}) { Text(icon) }
        Div({
            classes("dash-stat-value")
            style { color(Color(valueColor)) }
        }) { Text(value) }
        Div({ classes("dash-stat-label") }) { Text(label) }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Adherence Distribution Chart
//
// FIX: math is now correct.
// We distribute activePatients across 5 bands using realistic weights
// derived from the average adherence. No fake "count * 0.3" division
// by the percentage value.
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun AdherenceDistributionChart(averageAdherence: Int, activePatients: Int) {
    // Derive a plausible distribution from the average.
    // Higher averages push mass toward the top bands.
    // These are estimated buckets — clearly labelled as "est." in the UI.
    val avg = averageAdherence.coerceIn(0, 100)
    val total = activePatients.coerceAtLeast(1)

    // Weight bands based on average: if avg=80, most patients are in 80-89 and 90-100
    val w90 = (avg - 60).coerceAtLeast(0) * 0.015         // 90–100%
    val w80 = (avg - 50).coerceAtLeast(0) * 0.012         // 80–89%
    val w70 = 0.25                                          // 70–79% roughly stable
    val w60 = (110 - avg).coerceAtLeast(0) * 0.008        // 60–69%
    val wLow = (100 - avg).coerceAtLeast(0) * 0.01        // <60%
    val wSum = w90 + w80 + w70 + w60 + wLow

    fun toCount(w: Double) = ((w / wSum) * total).toInt().coerceAtLeast(0)

    val bands = listOf(
        Triple("90–100%", toCount(w90),  "#26A69A"),   // teal
        Triple("80–89%",  toCount(w80),  "#1A7A72"),   // teal deep
        Triple("70–79%",  toCount(w70),  "#F57F17"),   // amber
        Triple("60–69%",  toCount(w60),  "#E65100"),   // orange
        Triple("<60%",    toCount(wLow), "#C62828")    // danger
    )
    val maxCount = bands.maxOfOrNull { it.second }?.coerceAtLeast(1) ?: 1

    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) }}) {
        bands.forEach { (range, count, color) ->
            val barPct = (count * 100 / maxCount).coerceIn(4, 100)  // min 4% so bar is always visible
            Div({ classes("adh-row") }) {
                Div({ classes("adh-range-label") }) { Text(range) }
                Div({ classes("adh-track") }) {
                    Div({
                        classes("adh-bar")
                        style {
                            width(barPct.percent)
                            backgroundColor(Color(color))
                        }
                    }) {
                        if (barPct > 20) {
                            Span({ classes("adh-bar-label") }) { Text("$count") }
                        }
                    }
                }
                if (barPct <= 20) {
                    Div({ classes("adh-count") }) { Text("$count") }
                } else {
                    Div({ classes("adh-count") }) {}
                }
            }
        }

        Div({ style {
            marginTop(12.px); padding(12.px, 16.px)
            backgroundColor(Color(SURFACE)); borderRadius(10.px)
            fontSize(12.px); color(Color(INK_LIGHT)); lineHeight("1.5")
        }}) {
            Text("Estimated distribution based on avg adherence of $avg%. Actual breakdown requires per-patient data.")
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Real Insight Item — only shows derivable numbers, never fabricated ones
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun RealInsightItem(icon: String, label: String, detail: String, color: String) {
    Div({ style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(14.px)
        padding(14.px, 16.px); backgroundColor(Color(SURFACE)); borderRadius(12.px)
        marginBottom(10.px)
    }}) {
        Span({ style { fontSize(20.px); flexShrink(0) }}) { Text(icon) }
        Div({ style { flex(1) }}) {
            Div({ style {
                fontSize(11.px); fontWeight("700"); color(Color(INK_LIGHT))
                property("text-transform", "uppercase"); letterSpacing("0.08em"); marginBottom(2.px)
                fontFamily("DM Sans, sans-serif")
            }}) { Text(label) }
            Div({ style {
                fontSize(14.px); fontWeight("700"); color(Color(color))
                fontFamily("DM Sans, sans-serif")
            }}) { Text(detail) }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Alert row — replaces AlertCard; left-border accent, icon, count badge
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DashAlertRow(
    icon: String,
    title: String,
    description: String,
    accentColor: String,
    accentBg: String
) {
    Div({ classes("dash-alert"); style { property("border-left","4px solid $accentColor") }}) {
        Div({ style {
            width(40.px); height(40.px); borderRadius(10.px); flexShrink(0)
            backgroundColor(Color(accentBg))
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            fontSize(20.px)
        }}) { Text(icon) }
        Div({ style { flex(1) }}) {
            Div({ style {
                fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("800")
                color(Color(INK)); marginBottom(4.px)
            }}) { Text(title) }
            Div({ style {
                fontSize(13.px); color(Color(INK_MID)); fontFamily("DM Sans, sans-serif")
            }}) { Text(description) }
        }
    }
}